// Клієнт API адміністрування (U16) + хуки TanStack Query: сповіщення (/api/notifications), користувачі й ролі (ядро /api/users),
// журнал аудиту (ядро /api/audit + /api/admin/audit), правила розкладу, профілі ваг, черга листів, налаштування сеансу.
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AuditAction,
  EffectiveRuleItem,
  NotificationType,
  OutboxStatus,
  ResolveRulesOptions,
  Role,
  RuleKeyDef,
  RuleScope,
  SolverRules,
  SolverWeights,
} from '@unisched/shared';
import { api, type Paged, type QueryParams } from '@/lib/api';
import { downloadFile } from '@/lib/download';

const enc = encodeURIComponent;

// ---------------------------------------------------------------- типи відповідей API

export interface NotificationItem {
  id: string;
  type: NotificationType;
  title: string;
  body: string | null;
  link: string | null;
  entity: string | null;
  entityId: string | null;
  data: unknown;
  readAt: string | null;
  createdAt: string;
}

export interface NamedRef {
  id: string;
  code: string;
  name: string;
}

export interface UserRoleItem {
  id?: string;
  role: Role;
  facultyId: string | null;
  faculty?: NamedRef | null;
  departmentId: string | null;
  department?: NamedRef | null;
}

export interface UserItem {
  id: string;
  email: string;
  fullName: string;
  isActive: boolean;
  hasPassword: boolean;
  teacherId: string | null;
  teacher: { id: string; code: string; fullName: string } | null;
  studentGroupId: string | null;
  studentGroup: NamedRef | null;
  roles: UserRoleItem[];
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserPayload {
  email?: string;
  fullName?: string;
  password?: string;
  isActive?: boolean;
  teacherId?: string | null;
  studentGroupId?: string | null;
  roles?: Array<{ role: Role; facultyId?: string | null; departmentId?: string | null }>;
}

export interface AuditItem {
  id: string;
  actorId: string | null;
  action: AuditAction;
  entity: string;
  entityId: string | null;
  changes: Record<string, unknown> | null;
  comment: string | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  actor: { id: string; fullName: string; email: string } | null;
}

export interface RuleItem {
  id: string;
  code: string;
  key: string;
  name: string;
  value: unknown;
  scope: RuleScope;
  facultyId: string | null;
  faculty: NamedRef | null;
  course: number | null;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RulesResponse {
  catalog: RuleKeyDef[];
  items: RuleItem[];
  university: ResolveRulesOptions;
}

export interface EffectiveRulesResponse {
  scope: { facultyId: string | null; course: number | null };
  university: ResolveRulesOptions;
  rules: SolverRules;
  items: EffectiveRuleItem[];
}

export interface RulePayload {
  key?: string;
  name?: string;
  value?: unknown;
  scope?: RuleScope;
  facultyId?: string | null;
  course?: number | null;
  description?: string | null;
  isActive?: boolean;
}

export type WeightKey = keyof SolverWeights;

export interface WeightCatalogItem {
  key: WeightKey;
  label: string;
  hint: string;
  default: number;
  hard: boolean;
}

export interface WeightProfileItem {
  id: string;
  code: string;
  name: string;
  description: string | null;
  weights: Record<WeightKey, number>;
  isDefault: boolean;
  isSystem: boolean;
  isActive: boolean;
  sortOrder: number;
  usedInVersions: number;
  changedKeys: WeightKey[];
  createdAt: string;
  updatedAt: string;
}

export interface WeightsResponse {
  catalog: WeightCatalogItem[];
  items: WeightProfileItem[];
}

export interface OutboxItem {
  id: string;
  to: string;
  subject: string;
  status: OutboxStatus;
  attempts: number;
  lastError: string | null;
  nextAttemptAt: string;
  claimedAt: string | null;
  sentAt: string | null;
  notificationId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OutboxDetail extends OutboxItem {
  html: string;
  text: string | null;
}

export interface BulkTeachersResult {
  dryRun: boolean;
  total: number;
  created: number;
  items: Array<{ teacherId: string; fullName: string; email: string; userId: string | null }>;
  skipped: Array<{ teacherId: string; fullName: string; reason: string }>;
}

export interface AdminPolicy {
  sessionIdleTimeoutMin: number;
  auditAnonymizeAfterYears: number;
  notificationPollSec: number;
}

// ---------------------------------------------------------------- ендпоінти

export const adminApi = {
  notifications: (params?: QueryParams) => api.get<Paged<NotificationItem>>('/notifications', params),
  unreadCount: () => api.get<{ count: number }>('/notifications/unread-count'),
  markRead: (id: string) => api.post<void>(`/notifications/${enc(id)}/read`),
  markAllRead: () => api.post<{ updated: number }>('/notifications/read-all'),

  users: (params?: QueryParams) => api.get<Paged<UserItem>>('/users', params),
  user: (id: string) => api.get<UserItem>(`/users/${enc(id)}`),
  createUser: (body: UserPayload) => api.post<UserItem>('/users', body),
  updateUser: (id: string, body: UserPayload) => api.patch<UserItem>(`/users/${enc(id)}`, body),
  setRoles: (id: string, roles: NonNullable<UserPayload['roles']>) => api.put<UserItem>(`/users/${enc(id)}/roles`, { roles }),
  bulkTeachers: (body: { departmentId?: string | null; dryRun: boolean }) => api.post<BulkTeachersResult>('/admin/users/bulk-teachers', body),

  audit: (params?: QueryParams) => api.get<Paged<AuditItem>>('/audit', params),
  auditActors: (q: string, signal?: AbortSignal) =>
    api.get<Array<{ id: string; fullName: string; email: string }>>('/admin/audit/actors', { q: q || undefined }, { signal }),
  auditExport: (params?: QueryParams) => downloadFile('/admin/audit/export.xlsx', { params, fallbackName: 'Журнал аудиту.xlsx' }),

  rules: () => api.get<RulesResponse>('/admin/rules'),
  effectiveRules: (params: { facultyId?: string | null; course?: number | null }) =>
    api.get<EffectiveRulesResponse>('/admin/rules/effective', { facultyId: params.facultyId ?? undefined, course: params.course ?? undefined }),
  createRule: (body: RulePayload) => api.post<RuleItem>('/admin/rules', body),
  updateRule: (id: string, body: RulePayload) => api.patch<RuleItem>(`/admin/rules/${enc(id)}`, body),
  deleteRule: (id: string) => api.del<void>(`/admin/rules/${enc(id)}`),

  weights: () => api.get<WeightsResponse>('/admin/weight-profiles'),
  createProfile: (body: { code: string; name: string; description?: string | null; copyFromId?: string | null; weights?: Partial<Record<WeightKey, number>> }) =>
    api.post<WeightProfileItem>('/admin/weight-profiles', body),
  updateProfile: (id: string, body: { name?: string; description?: string | null; weights?: Partial<Record<WeightKey, number>>; isActive?: boolean }) =>
    api.patch<WeightProfileItem>(`/admin/weight-profiles/${enc(id)}`, body),
  resetProfile: (id: string) => api.post<WeightProfileItem>(`/admin/weight-profiles/${enc(id)}/reset`),
  makeDefaultProfile: (id: string) => api.post<WeightProfileItem>(`/admin/weight-profiles/${enc(id)}/make-default`),
  deleteProfile: (id: string) => api.del<void>(`/admin/weight-profiles/${enc(id)}`),

  outbox: (params?: QueryParams) => api.get<Paged<OutboxItem>>('/admin/outbox', params),
  outboxSummary: () => api.get<Record<OutboxStatus, number>>('/admin/outbox/summary'),
  outboxItem: (id: string) => api.get<OutboxDetail>(`/admin/outbox/${enc(id)}`),
  retryOutbox: (id: string) => api.post<OutboxItem>(`/admin/outbox/${enc(id)}/retry`),

  policy: () => api.get<AdminPolicy>('/admin/policy'),
};

export const adminKeys = {
  notifications: ['notifications'] as const,
  unread: ['notifications', 'unread-count'] as const,
  notificationList: (params: QueryParams) => ['notifications', 'list', params] as const,
  users: ['admin', 'users'] as const,
  user: (id: string) => ['admin', 'users', id] as const,
  audit: (params: QueryParams) => ['admin', 'audit', params] as const,
  rules: ['admin', 'rules'] as const,
  effective: (facultyId: string | null, course: number | null) => ['admin', 'rules', 'effective', facultyId, course] as const,
  weights: ['admin', 'weights'] as const,
  outbox: ['admin', 'outbox'] as const,
  policy: ['admin', 'policy'] as const,
};

// ---------------------------------------------------------------- хуки

/** Налаштування сеансу й опитування (кешується надовго — змінюється лише конфігурацією сервера). */
export function usePolicy() {
  return useQuery({ queryKey: adminKeys.policy, queryFn: adminApi.policy, staleTime: 10 * 60_000, retry: false });
}

/** Лічильник непрочитаних: оновлення кожні pollSec секунд (ТЗ 4.2.14.5 — не рідше ніж раз на 60 с). */
export function useUnreadCount(pollSec = 60) {
  return useQuery({
    queryKey: adminKeys.unread,
    queryFn: adminApi.unreadCount,
    refetchInterval: pollSec * 1000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
    retry: false,
  });
}

export function useNotifications(params: QueryParams, enabled = true) {
  return useQuery({
    queryKey: adminKeys.notificationList(params),
    queryFn: () => adminApi.notifications(params),
    placeholderData: keepPreviousData,
    enabled,
    staleTime: 0,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApi.markRead(id),
    meta: { silent: true },
    onSettled: () => qc.invalidateQueries({ queryKey: adminKeys.notifications }),
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminApi.markAllRead,
    meta: { errorMessage: 'Не вдалося позначити сповіщення' },
    onSettled: () => qc.invalidateQueries({ queryKey: adminKeys.notifications }),
  });
}
